import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";
import ListHeader from "@/common/components/ListHeader";
import ListFilters from "@/common/components/ListFilters";
import Table from "@/common/components/Table";
import TextField from "@/common/components/TextField";
import { DatePickerWithRange } from "@/common/components/ui/dateRangePicker";
import { useFilterDate } from "@/common/hooks/useFilterDate";
import { useFilterParams } from "@/common/hooks/useFilterParams";
import { Mapper } from "@/common/services/mapper";
import { SearchIcon, Trophy } from "lucide-react";
import useDebounce from "@/common/hooks/useDebounce";
import { useTmaisRaffles } from "@/common/hooks/queries/useTmaisRaffles";
import { TmaisRaffleData, TmaisRaffleTableItem } from "../../types/tmaisRaffles.type";
import { TmaisRafflesTransformer } from "../../services/tmaisRafflesTransformer";
import { tmaisRafflesColumns } from "../../constants/tmaisRafflesColumns.constant";
import { tmaisRafflesStatusOptions } from "../../constants/statusOptions.constant";
import { useEstablishmentCompanies } from "@/features/establishments/hooks/useEstablishmentCompanies";
import { EstablishmentCompanyTransformer } from "@/features/establishments/services/establishmentCompanyTransformer";
import { Option } from "@/common/types/option";

const TmaisRafflesList = () => {
  const { filterParams, setFilterParams } = useFilterParams();
  const [search, setSearch] = useDebounce(filterParams.search || '');
  const [searchEstablishment, setSearchEstablishment] = useDebounce('');

  const { date, formattedDate } = useFilterDate(filterParams);

  const { data: establishmentOptions } = useEstablishmentCompanies({
    filters: {
      search: searchEstablishment,
      tmaisModuleEnabled: true,
    },
    mapper: EstablishmentCompanyTransformer.optionTransformer({ valueFieldName: 'matrix_branch_id' }),
  });

  const {
    data: raffles = [],
    meta,
    isPending,
    error: rafflesError = {} as any,
    isFetching,
    refetch,
  } = useTmaisRaffles<TmaisRaffleTableItem[]>({
    filters: {
      search: search,
      page: filterParams.page || 1,
      status: filterParams.status?.value,
      creatorEstablishmentCompanyBranchId: filterParams.establishment?.value,
      availableFromGteq: formattedDate.from,
      availableFromLteq: formattedDate.to,
    },
    mapper: Mapper.mapWithTransformer<TmaisRaffleData[], TmaisRaffleTableItem[]>({
      transformer: TmaisRafflesTransformer.tableTransformer(),
    }),
  });

  const errorStatus = rafflesError?.status;
  const disableCreateButton = isPending || errorStatus === 403;

  return (
    <div className="flex flex-col gap-6">
      <ListHeader
        Icon={<Trophy className="size-10" strokeWidth={1.5} />}
        title="Sorteios"
        disableCreateButton={disableCreateButton}
        operationKlassToCheckPermission="Admin::Tmais::Raffles::Create"
        createButtonText="Cadastrar sorteio"
      />

      <ErrorMessageWrapper
        className="h-[calc(100vh-128px)]"
        statusCode={errorStatus}
        refetchFunction={refetch}
      >
        <div className="h-[calc(100vh-128px)] overflow-auto flex flex-col gap-6">
          <div className="flex flex-col gap-4">
            <TextField
              LeftIcon={<SearchIcon />}
              placeholder="Pesquisar"
              className="rounded-sm shadow-[4px_0px_16px_0px_rgba(0,_0,_0,_0.5)]"
              onChange={(e) => {
                const value = (e.target as HTMLInputElement).value;
                setSearch(value);
                setFilterParams((prev) => ({ ...prev, search: value, page: 1 }));
              }}
              value={filterParams.search || ''}
            />

            <div className="flex items-center gap-2">
              <DatePickerWithRange
                placeholder="Data de disponibilidade"
                date={date}
                setDate={(date) => setFilterParams((prev) => ({ ...prev, date }))}
              />

              <ListFilters
                filters={[
                  {
                    className: 'min-w-40',
                    onChange: (e: any) => setFilterParams((params) => ({ ...params, status: e?.value ? e : null })),
                    value: filterParams?.status || '',
                    options: [{ label: 'Todos', value: '' }, ...tmaisRafflesStatusOptions],
                    placeholder: 'Status',
                    dataTestid: 'filter_status',
                  },
                  {
                    className: 'min-w-40',
                    onChange: (e: any) =>
                      setFilterParams((params) => ({ ...params, establishment: e?.value ? e : null })),
                    onInputChange: setSearchEstablishment,
                    value: filterParams?.establishment || '',
                    options: [
                      { label: 'Todos', value: '' },
                      { label: 'Taurus (Global)', value: 'null' },
                      ...(establishmentOptions as Option[]),
                    ],
                    placeholder: 'Estabelecimento',
                    dataTestid: 'filter_establishment',
                  },
                ]}
              />
            </div>
          </div>

          <Table
            columns={tmaisRafflesColumns}
            rows={raffles || []}
            isLoadingData={isPending || isFetching}
            page={filterParams.page || 1}
            totalPages={meta?.total_pages || 1}
            totalCount={meta?.total || 0}
            hasPagination
            setPage={(page) =>
              setFilterParams((prev: any) => ({ ...prev, page }))
            }
            rowEmptyValue="--"
          />
        </div>
      </ErrorMessageWrapper>
    </div>
  );
};

export default TmaisRafflesList;
